<script setup lang="ts">
import { inject, Ref, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { DateTime } from 'luxon';
import { required } from '@vuelidate/validators';
import { resetTimer } from '@/utils/dateUtils';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';

const dialogRef = inject('dialogRef') as Ref;
const onClose = dialogRef?.value?.options?.emits?.onClose;

const dates = ref({
  start: null,
  end: null
});
const rowsOnly = ref(false);
const allRows = ref(false);
const minDateStart = ref(new Date(DateTime.utc().plus({ days: 1 })));
const minDateEnd = ref();
const rules = {
  start: { required },
  end: { required }
};
const v$ = useVuelidate(rules, dates.value);

const handleUpdateDates = () => {
  v$.value.$validate();
  if (!v$.value.$error) {
    const dateFormat = {
      start: resetTimer(dates.value.start),
      end: resetTimer(dates.value.end)
    };
    onClose({
      dates: dateFormat,
      rowsOnly: rowsOnly.value,
      allRows: allRows.value
    });
  }
};

watch(
  dates.value,
  (newVal: any) => {
    if (newVal.start && !newVal.end) {
      minDateEnd.value = new Date(newVal.start);
      dates.value.end = newVal.start;
    }
  },
  { deep: true }
);

const validateCheckbox = (type: string, event: any) => {
  if (type === 'rowsOnly' && event) {
    allRows.value = false;
  }
  if (type === 'allRows' && event) {
    rowsOnly.value = false;
  }
};
</script>

<template>
  <div>
    <div class="grid field mt-2">
      <div class="col col-6">
        <label for="startDate">{{ $t('create_quote.start_date') }}</label>
        <DatePicker id="startDate" dateFormat="dd-M-yy" :min-date="minDateStart" v-model="dates.start" autocomplete="off" class="w-full" :placeholder="$t('common.date_format')" :class="v$.start.$error && 'p-invalid'">
          <template #footer>
            <p class="text-center">
              <small>{{ $t('common.press_space') }}</small>
            </p>
          </template>
        </DatePicker>
        <p class="text-red-500 pt-1" v-if="v$.start.$error">{{ $t('validate.required') }}</p>
      </div>
      <div class="col col-6">
        <div class="flex">
          <div class="flex-grow-1">
            <label for="endDate">{{ $t('create_quote.end_date') }}</label>
            <DatePicker id="endDate" dateFormat="dd-M-yy" :min-date="minDateEnd" v-model="dates.end" autocomplete="off" class="w-full" :placeholder="$t('common.date_format')" :class="v$.end.$error && 'p-invalid'" :disabled="!dates.start">
              <template #footer>
                <p class="text-center">
                  <small>{{ $t('common.press_space') }}</small>
                </p>
              </template>
            </DatePicker>
            <p class="text-red-500 pt-1" v-if="v$.end.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="grid field mt-2">
      <div class="col col-6">
        <Checkbox inputId="rowsOnly" v-model="rowsOnly" :binary="true" @update:modelValue="validateCheckbox('rowsOnly', $event)" />
        <label class="ml-1" for="rowsOnly">{{ $t('management.tour.automated_tour.empty_rows_only') }}</label>
      </div>
      <div class="col col-6">
        <Checkbox inputId="allRows" v-model="allRows" :binary="true" @update:modelValue="validateCheckbox('allRows', $event)" />
        <label class="ml-1" for="allRows">{{ $t('management.tour.automated_tour.all_rows') }}</label>
      </div>
    </div>
    <div class="text-center">
      <Button :label="$t('management.tour.automated_tour.update')" @click="handleUpdateDates()"></Button>
    </div>
  </div>
</template>
